import { type States, states } from "@repo/lib/constants/states";
import type { Tenant } from "@repo/types";
import { ComboboxInput } from "@repo/ui/components/Base/ComboboxInput";
import { Button } from "@repo/ui/components/Base/Button";
import { Input } from "@repo/ui/components/Base/Input";
import { SelectItem } from "@repo/ui/components/Base/Select";
import { SelectInput } from "@repo/ui/components/Base/SelectInput";
import { useFormikContext } from "formik";
import { CheckCircleIcon, ClockIcon, XCircleIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { capitalize } from "@repo/shared/utils";
import { useDebounce } from "@repo/ui/components/Base/hooks/useDebounce";
import { useGetOrganizationTypes } from "@/hooks/queryHooks/configHooks";
import { tenantRepo } from "@/repositories/tenantRepo";
import { firstValueFrom } from "rxjs";
import {
  useGetMunicipalities,
  useGetStates,
} from "@/hooks/queryHooks/stripeHooks";

export const CreateOrgStep = ({ isSubmitting }: { isSubmitting: boolean }) => {
  const {
    values,
    handleChange,
    setFieldValue,
    errors,
    touched,
    validateField,
  } = useFormikContext<Partial<Tenant>>();
  const [friendlyIdValid, setFriendlyIdValid] = useState(false);
  const [friendlyIdErrorMessage, setFriendlyIdErrorMessage] =
    useState<string>();
  const { data: organizationTypes } = useGetOrganizationTypes();

  const { data: availableStates } = useGetStates();
  const { data: availableMunicipalities, loaded: municipalitiesLoaded } =
    useGetMunicipalities(values.organizationState ?? "");

  const availableTypes = useMemo(() => {
    if (!availableMunicipalities) return [];
    if ("error" in availableMunicipalities) return [];
    const types = new Map();
    availableMunicipalities.map((m) =>
      types.set(m.type, { id: m.typeId, type: m.type })
    );
    return Array.from(types.values()).map((t) => t);
  }, [availableMunicipalities]);

  function replaceNonAlphanumeric(inputString: string): string {
    // Use a regular expression to replace non-alphanumeric characters with a space
    return inputString.replace(/[^a-zA-Z0-9]+/g, "");
  }

  const onNameBlur = () => {
    setFriendlyIdValid(false);
    setFieldValue(
      "friendlyId",
      replaceNonAlphanumeric(values.name ?? "").toLowerCase()
    );
  };

  const handleValidateFriendlyId = useDebounce(async (friendlyId?: string) => {
    friendlyId ??= "";

    if (friendlyId.length < 6) {
      // ensure length is at least 6
      setFriendlyIdValid(false);
      setFriendlyIdErrorMessage(
        "Unique IDs must be at least 6 characters in length."
      );
    } else if (!/^[a-z0-9]+$/.test(friendlyId ?? "")) {
      // ensure id uses only alphanumeric characters
      setFriendlyIdValid(false);
      setFriendlyIdErrorMessage(
        "Only lowercase alphanumeric characters are allowed."
      );
    } else {
      // ensure id is unique
      const existing = await firstValueFrom(
        tenantRepo.getByFriendly(friendlyId)
      );
      if (existing?.friendlyId) {
        setFriendlyIdValid(false);
        setFriendlyIdErrorMessage("This unique ID is already in use");
        validateField("friendlyId");
      } else {
        setFriendlyIdValid(true);
        setFriendlyIdErrorMessage(undefined);
      }
    }
  }, 500);

  return (
    <>
      <h1 className="bg-tertiary rounded-lg text-xl text-white p-2">Step 1</h1>
      <h2 className="text-xl">Setup your organization to get started</h2>
      <p>
        We will walk you through the process of setting up your organization.
      </p>
      <p>
        It is important to understand the hierarchy that you will setup. The
        highest level of any organization is called{" "}
        <strong>Organization</strong>. This is the top level of any group in
        CiviDocs. Here you define the other groups in your organization and
        other administrative tasks such as subscription and user management.
      </p>
      <p>
        Each organization can have a number of teams (depending on your
        subscription level). <strong>Teams</strong> are the level where most of
        the functionality of CiviDocs exists. Here you can define letterheads,
        agenda templates, agenda sections, and create agendas with the
        associated meetings and agenda items. We will walk through setting these
        up in a later step.
      </p>
      <h3 className="text-xl">
        1.1. To get started, what would you like your{" "}
        <strong>Organization</strong> to be called?
      </h3>

      <Input
        label="*Organization Name"
        id="name"
        name="name"
        value={values.name}
        onBlur={() => onNameBlur()}
        onChange={handleChange}
        error={!!errors.name && touched.name}
        errorMessage={errors.name}
      />
      {values.name && values.name.length > 0 && (
        <>
          <h3 className="text-xl">
            1.2. Great! Each organization needs a unique ID.
          </h3>
          <p>
            We have generated a unique ID based on your name, but feel free to
            edit it if you wish
          </p>
          <Input
            label="Unique ID"
            id="friendlyId"
            name="friendlyId"
            value={values.friendlyId ?? ""}
            onChange={(event) => {
              handleChange(event);
              setFriendlyIdErrorMessage(undefined);
              setFriendlyIdValid(false);
              handleValidateFriendlyId(event.target.value);
            }}
            error={!!friendlyIdErrorMessage}
            errorMessage={friendlyIdErrorMessage}
            inserts={{
              after: values.friendlyId && (
                <div
                  className="flex ml-2 flex-row items-center gap-2"
                  onClick={() =>
                    handleValidateFriendlyId(values.friendlyId ?? "")
                  }
                >
                  <Button type="button" size="sm" className="self-end mb-[2px]">
                    Validate Unique ID
                  </Button>
                  <div className="self-end mb-[4px]">
                    {friendlyIdValid ? (
                      <CheckCircleIcon className="text-tertiary" size={32} />
                    ) : friendlyIdErrorMessage ? (
                      <XCircleIcon className="text-destructive" size={32} />
                    ) : (
                      <ClockIcon className="text-primary" size={32} />
                    )}
                  </div>
                </div>
              ),
            }}
          />
        </>
      )}

      {values.friendlyId &&
        friendlyIdValid &&
        organizationTypes?.organizationTypes && (
          <>
            <h3 className="text-xl">1.3. Select Your State</h3>
            <SelectInput
              label="State"
              onValueChange={(value) =>
                setFieldValue("organizationState", value)
              }
              options={
                availableStates?.map((state) => (
                  <SelectItem key={state} value={state}>
                    {states[state as keyof States]}
                  </SelectItem>
                )) ?? []
              }
              value={values.organizationState}
            />
          </>
        )}
      {values.friendlyId &&
        friendlyIdValid &&
        values.organizationState &&
        values.organizationState !== "" &&
        availableTypes && (
          <>
            {!municipalitiesLoaded ? (
              <Spinner />
            ) : (
              <>
                <h3 className="text-xl">1.4. Select Your Municipality Type</h3>
                <ComboboxInput
                  placeholder="Select Type"
                  emptyText="No Types Found"
                  label="Municipality Type"
                  id="municipality_type"
                  onChange={(value, id) => {
                    setFieldValue("organizationMunicipalityTypeId", `${id}`);
                    setFieldValue(
                      "organizationMunicipalityType",
                      value.split("-")[1]
                    );
                    setFieldValue("organizationMunicipalityName", undefined);
                  }}
                  options={availableTypes.map((type) => ({
                    value: `${type.id}-${type.type}`,
                    label: type.type,
                  }))}
                  value={values.organizationMunicipalityTypeId}
                />
              </>
            )}
            {values.organizationMunicipalityType &&
              values.organizationMunicipalityType !== "" &&
              availableMunicipalities && (
                <>
                  {!municipalitiesLoaded ? (
                    <Spinner />
                  ) : (
                    <>
                      <h3 className="text-xl">1.5. Select Your Municipality</h3>
                      <ComboboxInput
                        placeholder="Select Municipality"
                        emptyText="No Municipalities Found"
                        containerStyle="w-full"
                        id="municipality"
                        onChange={(value, id) => {
                          setFieldValue("organizationMunicipalityId", id);
                          setFieldValue(
                            "organizationMunicipalityName",
                            capitalize(value.split("-")[1])
                          );
                          setFieldValue(
                            "organizationMunicipalityPopulation",
                            availableMunicipalities.find(
                              (m) => `${m.id}` === value.split("-")[0]
                            )?.population ?? 0
                          );
                        }}
                        options={availableMunicipalities
                          .filter(
                            (m) =>
                              `${m.typeId}` ===
                              values.organizationMunicipalityTypeId?.split(
                                "-"
                              )[0]
                          )
                          .map((municipality) => ({
                            value: `${municipality.id}-${municipality.name}`,
                            label: `${municipality.name}${municipality.type !== "County" ? `, ${municipality.county} County` : ""}`,
                          }))}
                        value={values.organizationMunicipalityId}
                      />
                    </>
                  )}
                </>
              )}
          </>
        )}
      {values.friendlyId &&
        friendlyIdValid &&
        values.organizationMunicipalityType &&
        values.organizationMunicipalityName && (
          <Button type="submit" disabled={isSubmitting} className="mt-4">
            Continue
          </Button>
        )}
    </>
  );
};
